import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { fetchRooms, Room, getOccupiedRooms } from './logic/room'
import { TimetableData } from '@/types/timetable'
import { useTranslation } from 'react-i18next'

interface RoomSelectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (roomName: string) => void
  currentRoom?: string
  timetableData?: TimetableData
  currentClass?: string
  day?: string
  time?: string
}

export function RoomSelectionDialog({
  open,
  onOpenChange,
  onSelect,
  currentRoom = '',
  timetableData,
  currentClass = '',
  day = '',
  time = '',
}: RoomSelectionDialogProps) {
  const [rooms, setRooms] = useState<Room[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedRoom, setSelectedRoom] = useState(currentRoom)
  const [occupiedRooms, setOccupiedRooms] = useState<string[]>([])
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder.RoomSelectionDialog' })

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)

        // Load all rooms
        const roomsData = await fetchRooms()
        setRooms(roomsData)

        // Get occupied rooms if we have all required data
        if (timetableData && day && time && currentClass) {
          const occupied = getOccupiedRooms(
            timetableData,
            day,
            time,
            currentClass
          )
          setOccupiedRooms(occupied)
       // console.logOccupied rooms at', day, time, ':', occupied)
        }
      } catch (error) {
        // console.error('Error loading rooms:', error)
      } finally {
        setLoading(false)
      }
    }

    if (open) {
      loadData()
      setSelectedRoom(currentRoom)
    }
  }, [open, currentRoom, timetableData, day, time, currentClass])

  const getRoomStatus = (roomName: string) => {
    if (roomName === currentRoom) return 'current'
    if (occupiedRooms.includes(roomName)) return 'occupied'
    return 'available'
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[95vw] md:w-full">
        <DialogHeader>
          <DialogTitle>{t('buttons.select')}</DialogTitle>
          <div className="text-sm text-muted-foreground">
            {day} {t('labels.at')} {time} • {currentClass}
          </div>
        </DialogHeader>

        <div className="max-h-[400px] overflow-y-auto space-y-2">
          {loading ? (
            <div className="text-center py-4">{t('loadingRooms')}</div>
          ) : (
            rooms.map((room) => {
              const status = getRoomStatus(room.name)
              return (
                <div
                  key={room.name}
                  className={`p-3 border rounded-md transition-colors ${
                    status === 'current'
                      ? 'bg-yellow-50 border-yellow-200'
                      : status === 'occupied'
                        ? 'bg-red-50 border-red-200 cursor-not-allowed'
                        : 'hover:bg-accent cursor-pointer'
                  } ${selectedRoom === room.name ? 'ring-2 ring-primary' : ''}`}
                  onClick={() =>
                    status !== 'occupied' && setSelectedRoom(room.name)
                  }
                >
                  <div className="font-medium flex items-center">
                    {room.name}
                    {status === 'current' && (
                      <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                        {t('labels.current')}
                      </span>
                    )}
                    {status === 'occupied' && (
                      <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full">
                        {t('labels.occupied')}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {room.type} • {t('labels.seats', { count: room.capacity })}
                  </div>
                </div>
              )
            })
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('buttons.cancel')}
          </Button>
          <Button
            onClick={() => {
              if (selectedRoom) {
                onSelect(selectedRoom)
                onOpenChange(false)
              }
            }}
            disabled={!selectedRoom || occupiedRooms.includes(selectedRoom)}
          >
            {t('buttons.select')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
